import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Car, Building2, Users, TrendingUp, Plus, ArrowRight, MessageSquare, Phone } from 'lucide-react';
import { dashboardService, vehicleService, leadService } from '../services/api';
import QuickNotes from '../components/QuickNotes';

const DashboardPage = () => {
  const { user, isSuperAdmin, isAgencyAdmin } = useAuth();
  const [stats, setStats] = useState({
    agencies: 0,
    users: 0,
    vehicles: 0,
    published: 0,
    leads: 0,
    newLeads: 0
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [isSuperAdmin]);

  const loadData = async () => {
    try {
      if (isSuperAdmin) {
        // SUPER_ADMIN sees global stats
        const globalStats = await dashboardService.getGlobalStats();
        setStats({
          agencies: globalStats.totalAgencies,
          users: globalStats.totalUsers,
          vehicles: globalStats.totalVehicles,
          published: globalStats.publishedVehicles,
          leads: globalStats.totalLeads,
          newLeads: globalStats.newLeads
        });
        
        // Get recent leads
        try {
          const leads = await leadService.getAll({ limit: 5 });
          setRecentLeads(leads.slice(0, 5));
        } catch (e) {
          setRecentLeads([]);
        }
      } else {
        // Agency users see their own stats
        const [vehicles, leads] = await Promise.all([
          vehicleService.getAll().catch(() => []),
          leadService.getAll().catch(() => [])
        ]);
        
        const publishedVehicles = vehicles.filter(v => v.status === 'published').length;
        const newLeads = leads.filter(l => l.status === 'new').length;
        
        setStats({
          agencies: 0,
          users: 0,
          vehicles: vehicles.length,
          published: publishedVehicles,
          leads: leads.length,
          newLeads: newLeads
        });
        
        setRecentLeads(leads.slice(0, 5));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserDisplayName = () => {
    if (user?.fullName) return user.fullName;
    if (user?.profile?.fullName) return user.profile.fullName;
    return user?.email || 'Usuario';
  };

  const statsConfig = isSuperAdmin
    ? [
        { icon: Building2, label: 'Agencias', value: stats.agencies, color: 'text-blue-600', bgColor: 'bg-blue-50' },
        { icon: Users, label: 'Usuarios', value: stats.users, color: 'text-green-600', bgColor: 'bg-green-50' },
        { icon: Car, label: 'Vehículos', value: stats.vehicles, color: 'text-purple-600', bgColor: 'bg-purple-50' },
        { icon: MessageSquare, label: 'Leads Nuevos', value: stats.newLeads, color: 'text-orange-600', bgColor: 'bg-orange-50' }
      ]
    : [
        { icon: Car, label: 'Mis Vehículos', value: stats.vehicles, color: 'text-blue-600', bgColor: 'bg-blue-50' },
        { icon: TrendingUp, label: 'Publicados', value: stats.published, color: 'text-green-600', bgColor: 'bg-green-50' },
        { icon: MessageSquare, label: 'Total Leads', value: stats.leads, color: 'text-purple-600', bgColor: 'bg-purple-50' },
        { icon: Phone, label: 'Leads Nuevos', value: stats.newLeads, color: 'text-orange-600', bgColor: 'bg-orange-50' }
      ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-slate-600 mt-2">
          Bienvenido de vuelta, {getUserDisplayName()}
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <>
          {/* Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statsConfig.map((stat, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                      <stat.icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
                  <p className="text-sm text-slate-600">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Columna principal */}
            <div className="lg:col-span-2 space-y-6">
              {/* Accesos rápidos */}
              <Card>
                <CardHeader>
                  <CardTitle>Accesos Rápidos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {isSuperAdmin ? (
                      <>
                        <Link to="/app/admin/agencies/new">
                          <Button variant="outline" className="w-full h-24 flex flex-col gap-2">
                            <Building2 className="w-8 h-8" />
                            <span>Nueva Agencia</span>
                          </Button>
                        </Link>
                        <Link to="/app/admin/agencies">
                          <Button variant="outline" className="w-full h-24 flex flex-col gap-2">
                            <Building2 className="w-8 h-8" />
                            <span>Ver Agencias</span>
                          </Button>
                        </Link>
                        <Link to="/app/admin/users">
                          <Button variant="outline" className="w-full h-24 flex flex-col gap-2">
                            <Users className="w-8 h-8" />
                            <span>Gestionar Usuarios</span>
                          </Button>
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link to="/app/cars/new">
                          <Button variant="outline" className="w-full h-24 flex flex-col gap-2">
                            <Plus className="w-8 h-8" />
                            <span>Nuevo Vehículo</span>
                          </Button>
                        </Link>
                        <Link to="/app/cars">
                          <Button variant="outline" className="w-full h-24 flex flex-col gap-2">
                            <Car className="w-8 h-8" />
                            <span>Mis Vehículos</span>
                          </Button>
                        </Link>
                        <Link to="/app/leads">
                          <Button variant="outline" className="w-full h-24 flex flex-col gap-2">
                            <MessageSquare className="w-8 h-8" />
                            <span>Ver Leads</span>
                          </Button>
                        </Link>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Leads recientes */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Leads Recientes</CardTitle>
                  <Link to="/app/leads">
                    <Button variant="ghost" size="sm">
                      Ver todos <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {recentLeads.length === 0 ? (
                    <p className="text-sm text-slate-500">No hay leads recientes</p>
                  ) : (
                    <div className="space-y-3">
                      {recentLeads.map((lead) => (
                        <div key={lead.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">
                              {lead.customer?.fullName || 'Sin nombre'}
                            </p>
                            <p className="text-sm text-slate-500 truncate max-w-xs">
                              {lead.message}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              lead.status === 'new' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {lead.status === 'new' ? 'Nuevo' : lead.status}
                            </span>
                            <p className="text-xs text-slate-500 mt-1">
                              {lead.source}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar con notas */}
            <div className="lg:col-span-1">
              <QuickNotes />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
